/** E3 (spec 2026-09-18 D15, §6 Edit): the author's edit session on a CUSTOM
 * row. Unlike processConfigStore there is no base and nothing is sparse —
 * the whole document IS the state, and it IS the draft `PUT /draft` stores.
 * `steps` key order is the reading order, so order is content here: the
 * dirtiness check is order-sensitive on purpose.
 *
 * The server's models are strict (`extra=forbid`, `min_length=1`), so a
 * cleared input deletes its key rather than storing an empty text. */
import { create } from 'zustand'
import type {
  OrderWorkflowDefinitionJson,
  ProcessDefinitionDiff,
  ProcessExitJson,
  ProcessFieldJson,
  ProcessStepJson,
  ProcessWorkflowDefinitionJson,
} from '@/types/workflowDefinition'
import { normalizeExit } from '@/utils/workflowOverview'

/** The two kinds the draft lane accepts (`_load_authorable_workflow`). An
 * order document is edited raw only (form editing is E9). */
export type AuthorableDefinitionJson = ProcessWorkflowDefinitionJson | OrderWorkflowDefinitionJson

type SeatJson = ProcessWorkflowDefinitionJson['seats'][string]
type LaneJson = ProcessWorkflowDefinitionJson['lanes'][string]

/** Why a step cannot be removed: the steps whose exits target it, and the
 * document-level pointers that name it. */
export interface RemoveStepRefusal {
  blockedBy: string[]
  pinnedAs: ('entry' | 'revision.resume_at')[]
}

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)
const isEmptyObject = (v: unknown) =>
  !!v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0

/** Sets `value` at `path`; a cleared value ('' / null / undefined) deletes
 * the leaf, and every object left empty along the path goes with it. */
function writePath(target: Record<string, unknown>, path: string[], value: unknown): void {
  const [head, ...rest] = path
  if (rest.length === 0) {
    if (value === '' || value == null) delete target[head]
    else target[head] = value
    return
  }
  const child = target[head]
  const next: Record<string, unknown> =
    child && typeof child === 'object' && !Array.isArray(child) ? (child as Record<string, unknown>) : {}
  target[head] = next
  writePath(next, rest, value)
  if (isEmptyObject(next)) delete target[head]
}

interface ProcessDraftState {
  workflowId: string | null
  doc: AuthorableDefinitionJson | null
  /** The last document the server stored (or the live definition when no
   * draft exists yet) — the clean point. */
  saved: AuthorableDefinitionJson | null
  /** The stored draft's stamp; null = no stored draft, so nothing to publish. */
  expectedUpdatedAt: string | null
  /** The live version this session's document came from
   * (`current_version_number` on the draft read); null = never published.
   * Every write asserts it (SA-404): a publish CLEARS the draft, so "no
   * stored draft" alone cannot tell that a newer version landed meanwhile. */
  baseVersionNumber: number | null
  editing: boolean
  /** SA-1060: the server's last `process_definition_diff` for this draft,
   * written by WorkflowDraftBar on every validate success and read for the
   * cards' "edited" marks (ruling Q3). Kept across edits on purpose — the
   * marks follow the server's last answer until the next one lands. */
  diff: ProcessDefinitionDiff | null
  setDiff(diff: ProcessDefinitionDiff | null): void
  begin(
    workflowId: string,
    doc: AuthorableDefinitionJson,
    expectedUpdatedAt: string | null,
    baseVersionNumber: number | null,
  ): void
  stop(): void
  /** `workflowId` is the session the save STARTED in; an answer for any other
   * session (after Exit, or a move to another workflow) changes nothing (SA-1410). */
  markSaved(doc: AuthorableDefinitionJson, stamp: string | null, workflowId: string): void
  /** Shallow patch; a key given as `undefined` is removed. */
  setStep(stepId: string, patch: Partial<ProcessStepJson>): void
  /** `path` is relative to the step, e.g. `['executor', 'seat']` — the
   * address shape ProcessStepEditor reports in both modes. */
  setStepPath(stepId: string, path: string[], value: unknown): void
  /** Inserts after `afterId` (else last). An id already in use is ignored. */
  addStep(stepId: string, step: ProcessStepJson, afterId?: string): void
  /** Refused (document unchanged) while anything in the result is non-empty. */
  removeStep(stepId: string): RemoveStepRefusal
  moveStep(stepId: string, direction: 'up' | 'down'): void
  /** `null` removes the exit. */
  setExit(stepId: string, name: string, exit: ProcessExitJson | null): void
  setField(fieldId: string, spec: ProcessFieldJson | null): void
  setSeat(seatId: string, spec: SeatJson | null): void
  setLane(laneId: string, spec: LaneJson | null): void
  setEntry(stepId: string): void
  /** The raw editor's write: replaces the whole document. */
  setRaw(doc: AuthorableDefinitionJson): void
  isDirty(): boolean
}

/** The session's document when it is a process, else null. */
export function processDoc(state: Pick<ProcessDraftState, 'doc'>): ProcessWorkflowDefinitionJson | null {
  return state.doc?.kind === 'process' ? state.doc : null
}

export const useProcessDraftStore = create<ProcessDraftState>((set, get) => {
  /** Applies `mutate` to a copy of the process document; a no-op on an
   * order document or outside a session. */
  const edit = (mutate: (doc: ProcessWorkflowDefinitionJson) => void) => {
    const current = processDoc(get())
    if (!current) return
    const next = clone(current)
    mutate(next)
    set({ doc: next })
  }
  const setIn = <T,>(section: 'fields' | 'seats' | 'lanes', id: string, spec: T | null) =>
    edit((doc) => {
      const map = { ...(doc[section] as Record<string, unknown>) }
      if (spec === null) delete map[id]
      else map[id] = clone(spec)
      ;(doc as unknown as Record<string, unknown>)[section] = map
    })

  return {
    workflowId: null,
    doc: null,
    saved: null,
    expectedUpdatedAt: null,
    baseVersionNumber: null,
    editing: false,
    diff: null,
    begin: (workflowId, doc, expectedUpdatedAt, baseVersionNumber) =>
      set({ workflowId, doc: clone(doc), saved: clone(doc), expectedUpdatedAt, baseVersionNumber, editing: true, diff: null }),
    stop: () =>
      set({
        workflowId: null, doc: null, saved: null, expectedUpdatedAt: null, baseVersionNumber: null, editing: false, diff: null,
      }),
    markSaved: (doc, stamp, workflowId) => {
      if (get().workflowId !== workflowId) return
      set({ saved: clone(doc), expectedUpdatedAt: stamp })
    },
    setDiff: (diff) => set({ diff }),
    setStep: (stepId, patch) =>
      edit((doc) => {
        const step = doc.steps[stepId] as unknown as Record<string, unknown> | undefined
        if (!step) return
        for (const [key, value] of Object.entries(patch)) {
          if (value === undefined) delete step[key]
          else step[key] = clone(value)
        }
      }),
    setStepPath: (stepId, path, value) =>
      edit((doc) => {
        const step = doc.steps[stepId] as unknown as Record<string, unknown> | undefined
        if (!step) return
        writePath(step, path, value)
        // A due is its `after` (strict DurationSpec): the calendar alone is no due.
        const due = step.due as { after?: string } | undefined
        if (due && !due.after) delete step.due
      }),
    addStep: (stepId, step, afterId) =>
      edit((doc) => {
        if (stepId in doc.steps) return
        const entries = Object.entries(doc.steps)
        const at = afterId ? entries.findIndex(([id]) => id === afterId) : -1
        entries.splice(at === -1 ? entries.length : at + 1, 0, [stepId, clone(step)])
        doc.steps = Object.fromEntries(entries)
      }),
    removeStep: (stepId) => {
      const doc = processDoc(get())
      const refusal: RemoveStepRefusal = { blockedBy: [], pinnedAs: [] }
      if (!doc || !(stepId in doc.steps)) return refusal
      refusal.blockedBy = Object.entries(doc.steps)
        .filter(([id, s]) => id !== stepId && Object.values(s.exits ?? {}).some((e) => normalizeExit(e).to === stepId))
        .map(([id]) => id)
      if (doc.entry === stepId) refusal.pinnedAs.push('entry')
      if (doc.revision?.resume_at === stepId) refusal.pinnedAs.push('revision.resume_at')
      if (refusal.blockedBy.length === 0 && refusal.pinnedAs.length === 0) {
        edit((d) => {
          delete d.steps[stepId]
        })
      }
      return refusal
    },
    moveStep: (stepId, direction) =>
      edit((doc) => {
        const entries = Object.entries(doc.steps)
        const from = entries.findIndex(([id]) => id === stepId)
        const to = direction === 'up' ? from - 1 : from + 1
        if (from === -1 || to < 0 || to >= entries.length) return
        ;[entries[from], entries[to]] = [entries[to], entries[from]]
        doc.steps = Object.fromEntries(entries)
      }),
    setExit: (stepId, name, exit) =>
      edit((doc) => {
        const step = doc.steps[stepId]
        if (!step) return
        if (exit === null) delete step.exits[name]
        else step.exits = { ...step.exits, [name]: clone(exit) }
      }),
    setField: (fieldId, spec) => setIn('fields', fieldId, spec),
    setSeat: (seatId, spec) => setIn('seats', seatId, spec),
    setLane: (laneId, spec) => setIn('lanes', laneId, spec),
    setEntry: (stepId) =>
      edit((doc) => {
        doc.entry = stepId
      }),
    setRaw: (doc) => {
      if (get().editing) set({ doc: clone(doc) })
    },
    isDirty: () => !same(get().doc, get().saved),
  }
})
